import { useEffect, useRef, useState, type ReactNode } from "react";
import { CheckCircle2, Loader2, ShieldCheck, Mail, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ensureTurnstileScript } from "@/lib/turnstile";
// Generated by orval from /founder-email in openapi.yaml (run codegen).
import { useRevealFounderEmail } from "@workspace/api-client-react";

// `window.turnstile` is typed in src/types/turnstile.d.ts (shared with Contact).

type Props = {
  slug: string;
  firstName: string;
  trigger: ReactNode;
};

/**
 * Turnstile-gated email reveal.
 *
 * The address is NOT in the client bundle. On a successful Turnstile solve we
 * POST the token to /api/founder-email, which verifies it server-side and
 * returns the address. Scrapers that never solve the challenge never receive it.
 */
export function FounderEmailReveal({ slug, firstName, trigger }: Props) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
  const reveal = useRevealFounderEmail();

  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  // Inject the Turnstile script once (shared loader, also used by Contact).
  useEffect(() => {
    if (siteKey) ensureTurnstileScript();
  }, [siteKey]);

  // Render the widget when the dialog is open and we don't yet have the email.
  useEffect(() => {
    if (!open || email || !siteKey) return;
    let tries = 0;
    let cancelled = false;

    const tryRender = () => {
      if (cancelled) return;
      const el = containerRef.current;
      if (!el || widgetIdRef.current !== null) return;
      if (!window.turnstile?.render) {
        if (tries++ < 40) setTimeout(tryRender, 150);
        return;
      }
      if (el.childElementCount > 0) return;
      widgetIdRef.current = window.turnstile.render(el, {
        sitekey: siteKey,
        theme: "dark",
        callback: (token: string) => {
          reveal.mutate(
            { data: { slug, turnstileToken: token } },
            {
              onSuccess: (res) => setEmail(res.email),
              onError: () => {
                // Token is single-use; reset so the user can retry.
                if (widgetIdRef.current) window.turnstile?.reset(widgetIdRef.current);
              },
            },
          );
        },
      });
    };
    tryRender();

    return () => {
      cancelled = true;
    };
  }, [open, email, siteKey, slug, reveal]);

  const cleanup = () => {
    if (window.turnstile && widgetIdRef.current) {
      try {
        window.turnstile.remove(widgetIdRef.current);
      } catch {
        /* noop */
      }
    }
    widgetIdRef.current = null;
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      cleanup();
      reveal.reset();
      // Keep `email` so re-opening shows the result instantly; clear it here
      // instead if you'd rather force a fresh challenge every open.
    }
  };

  const copyEmail = async () => {
    if (!email) return;
    const flag = () => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    };
    try {
      await navigator.clipboard.writeText(email);
      flag();
    } catch {
      // Fallback for restricted/insecure contexts.
      const ta = document.createElement("textarea");
      ta.value = email;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {
        /* noop */
      }
      document.body.removeChild(ta);
      flag();
    }
  };

  const mailto = email
    ? `mailto:${email}?subject=${encodeURIComponent("Message from Pax Magellanic Website")}`
    : "#";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-card border-border sm:max-w-md">
        {email ? (
          <div className="text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/15 flex items-center justify-center text-primary">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-center">
                Verified — here it is
              </DialogTitle>
              <DialogDescription className="text-center">
                Reach {firstName} at the address below.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-5 mb-4 rounded-md border border-border bg-background px-4 py-3.5 font-mono text-[17px] text-foreground [overflow-wrap:anywhere]">
              {email}
            </div>
            <div className="flex gap-2.5">
              <Button asChild className="flex-1 gap-2">
                <a href={mailto}>
                  <Mail className="w-4 h-4" /> Compose
                </a>
              </Button>
              <Button variant="outline" className="flex-1 gap-2" onClick={copyEmail}>
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copied!" : "Copy"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-background border border-border flex items-center justify-center text-primary">
              <ShieldCheck className="w-7 h-7" />
            </div>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-center">
                Quick human check
              </DialogTitle>
              <DialogDescription className="text-center">
                We hide email addresses from scrapers. Clear the check and {firstName}'s
                address appears.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-5 flex justify-center min-h-[68px]">
              {siteKey ? (
                <div className="relative">
                  {/* Keep the widget host mounted across the pending state so the
                      rendered Turnstile widget survives and can be reset for a
                      retry on error. The spinner overlays it while verifying. */}
                  <div ref={containerRef} className={reveal.isPending ? "invisible" : undefined} />
                  {reveal.isPending && (
                    <div
                      role="status"
                      aria-live="polite"
                      className="absolute inset-0 flex items-center justify-center gap-2 text-muted-foreground"
                    >
                      <Loader2 className="w-5 h-5 animate-spin" /> Verifying…
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-destructive">
                  VITE_TURNSTILE_SITE_KEY is not configured.
                </p>
              )}
            </div>

            {reveal.isError && (
              <p role="alert" className="mt-3 text-sm text-destructive">
                Check failed. Please try again.
              </p>
            )}

            <p className="mt-5 font-mono text-[11px] tracking-[0.1em] text-muted-foreground/70">
              PROTECTED BY CLOUDFLARE TURNSTILE
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
